// src/components/dashboard/ProductsAboutToExpireChart.js
import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// Register the necessary components for Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const ProductsAboutToExpireChart = () => {
    // Mock data for products about to expire
    // Expiry dates are relative to now for sorting purposes
    const expiringProductsData = [
        { name: 'Organic Milk (2L)', quantity: 15, daysLeft: 3 },
        { name: 'Fresh Salmon Fillets', quantity: 8, daysLeft: 1 },
        { name: 'Blueberry Muffins', quantity: 20, daysLeft: 2 },
        { name: 'Seasonal Fruit Pack', quantity: 12, daysLeft: 4 },
        { name: 'Yogurt Assortment', quantity: 25, daysLeft: 5 },
        { name: 'Gluten-Free Bread', quantity: 10, daysLeft: 7 },
    ];

    // Sort by daysLeft (closest expiry first) and then by quantity
    expiringProductsData.sort((a, b) => {
        if (a.daysLeft !== b.daysLeft) {
            return a.daysLeft - b.daysLeft;
        }
        return a.quantity - b.quantity; // Ascending quantity for horizontal chart
    });

    const data = {
        labels: expiringProductsData.map(p => `${p.name} (${p.daysLeft} days)`),
        datasets: [
            {
                label: 'Quantity Remaining',
                data: expiringProductsData.map(p => p.quantity),
                backgroundColor: (context) => {
                    const daysLeft = expiringProductsData[context.dataIndex].daysLeft;
                    if (daysLeft <= 3) return 'rgba(239, 68, 68, 0.8)'; // Red for urgent (daysLeft <= 3)
                    if (daysLeft <= 7) return 'rgba(251, 191, 36, 0.8)'; // Amber for warning (daysLeft <= 7)
                    return 'rgba(59, 130, 246, 0.8)'; // Blue for others
                },
                borderColor: (context) => {
                    const daysLeft = expiringProductsData[context.dataIndex].daysLeft;
                    if (daysLeft <= 3) return 'rgba(239, 68, 68, 1)';
                    if (daysLeft <= 7) return 'rgba(251, 191, 36, 1)';
                    return 'rgba(59, 130, 246, 1)';
                },
                borderWidth: 1,
            },
        ],
    };

    const options = {
        indexAxis: 'y', // Horizontal bar chart
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // No need for legend as color indicates urgency
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                bodyFont: { size: 14, family: 'Inter, sans-serif' },
                titleFont: { size: 16, family: 'Inter, sans-serif', weight: 'bold' },
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        const product = expiringProductsData[context.dataIndex];
                        label += `${product.quantity} items (Expires in ${product.daysLeft} days)`;
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(209, 213, 219, 0.4)',
                },
                ticks: {
                    color: '#4b5563',
                    font: { size: 12, family: 'Inter, sans-serif' },
                    callback: function(value) {
                        return value.toLocaleString();
                    }
                },
                title: {
                    display: true,
                    text: 'Quantity',
                    color: '#4b5563',
                    font: { size: 14, family: 'Inter, sans-serif', weight: 'bold' }
                }
            },
            y: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#4b5563',
                    font: { size: 12, family: 'Inter, sans-serif' }
                }
            },
        },
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6 h-96 flex flex-col">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Products About to Expire</h3>
            <p className="text-sm text-gray-500 mb-4">Inventory nearing expiry (color indicates urgency)</p>
            <div className="flex-1 relative">
                <Bar data={data} options={options} />
            </div>
            <div className="flex justify-center space-x-4 text-sm mt-4">
        <span className="flex items-center text-gray-600">
          <span className="w-3 h-3 rounded-full bg-red-500 mr-1"></span> &le; 3 Days
        </span>
                <span className="flex items-center text-gray-600">
          <span className="w-3 h-3 rounded-full bg-amber-400 mr-1"></span> &le; 7 Days
        </span>
                <span className="flex items-center text-gray-600">
          <span className="w-3 h-3 rounded-full bg-blue-500 mr-1"></span> &gt; 7 Days
        </span>
            </div>
        </div>
    );
};

export default ProductsAboutToExpireChart;